import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Chat history interfaces matching backend structure
export interface ChatMessage {
  id: string;
  content: string;
  source: 'user' | 'keai';
  timestamp: string;
  session_id?: string;
}

export interface ChatGroup {
  id: string;
  title: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  messages?: ChatMessage[];
}

export interface ChatHistoryState {
  chatGroups: ChatGroup[];
  activeChatGroup: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatHistoryState = {
  chatGroups: [],
  activeChatGroup: null,
  isLoading: false,
  error: null,
};

const chatHistorySlice = createSlice({
  name: 'chatHistory',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Set chat groups from backend
    setChatGroups: (state, action: PayloadAction<ChatGroup[]>) => {
      // Sort chat groups by updated_at timestamp, most recent first
      state.chatGroups = action.payload.sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
      
      state.isLoading = false;
      state.error = null;
    },

    clearChat: (state) => {
      state.activeChatGroup = null;
      state.error = null;
    },

    // Add new chat group
    addChatGroup: (state, action: PayloadAction<ChatGroup>) => {
      // Check if group already exists
      const existingGroupIndex = state.chatGroups.findIndex(group => group.id === action.payload.id);
      
      if (existingGroupIndex !== -1) {
        // Update existing group
        state.chatGroups[existingGroupIndex] = {
          ...state.chatGroups[existingGroupIndex],
          ...action.payload,
          updated_at: new Date().toISOString()
        };
        // Move updated group to the top
        const updatedGroup = state.chatGroups[existingGroupIndex];
        state.chatGroups.splice(existingGroupIndex, 1);
        state.chatGroups.unshift(updatedGroup);
      } else {
        // Add new group to the beginning of the array
        state.chatGroups.unshift(action.payload);
      }
      
      // Set as active chat group
      state.activeChatGroup = action.payload.id;
      state.isLoading = false;
      state.error = null;
    },

    // Add a temporary chat group with "New Chat" title
    addTemporaryChatGroup: (state, action: PayloadAction<string>) => {
      const tempGroup: ChatGroup = {
        id: action.payload,
        title: 'New Chat',
        user_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        messages: [],
      };
      
      // Check if this temporary group already exists
      const existingIndex = state.chatGroups.findIndex(group => group.id === action.payload);
      if (existingIndex === -1) {
        state.chatGroups.unshift(tempGroup);
      }
      
      state.activeChatGroup = action.payload;
    },

    // Update chat group messages
    setChatMessages: (state, action: PayloadAction<{ chatGroupId: string; messages: ChatMessage[] }>) => {
      const chatGroup = state.chatGroups.find(group => group.id === action.payload.chatGroupId);
      if (chatGroup) {
        chatGroup.messages = action.payload.messages;
      }
    },

    // Delete chat group
    deleteChatGroup: (state, action: PayloadAction<string>) => {
      state.chatGroups = state.chatGroups.filter(group => group.id !== action.payload);
      if (state.activeChatGroup === action.payload) {
        state.activeChatGroup = null;
      }
    },

    // Set active chat group
    setActiveChatGroup: (state, action: PayloadAction<string | null>) => {
      state.activeChatGroup = action.payload;
    },

    // Add message to specific chat group
    addMessage: (state, action: PayloadAction<{ chatGroupId: string; message: ChatMessage }>) => {
      const chatGroup = state.chatGroups.find(group => group.id === action.payload.chatGroupId);
      if (chatGroup) {
        if (!chatGroup.messages) {
          chatGroup.messages = [];
        }
        chatGroup.messages.push(action.payload.message);
        
        // Update the chat group's timestamp
        chatGroup.updated_at = new Date().toISOString();
        
        // Move the chat group to the top of the list
        const groupIndex = state.chatGroups.findIndex(g => g.id === action.payload.chatGroupId);
        if (groupIndex > 0) {
          const [movedGroup] = state.chatGroups.splice(groupIndex, 1);
          state.chatGroups.unshift(movedGroup);
        }
      }
    },

    // Clear all chat history
    clearChatHistory: (state) => {
      state.chatGroups = [];
      state.activeChatGroup = null;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setChatGroups,
  addChatGroup,
  addTemporaryChatGroup,
  setChatMessages,
  deleteChatGroup,
  setActiveChatGroup,
  addMessage,
  clearChatHistory,
  clearChat,
} = chatHistorySlice.actions;

export default chatHistorySlice.reducer; 